import { computed, onMounted, ref, watch } from "vue";
import { useAuthStore } from "@/store/auth";
import { apolloClient } from "@/graphql/client";
import {
  GetClubMetaByShortNameDocument,
  type GetClubMetaByShortNameQuery,
  type GetClubMetaByShortNameQueryVariables,
} from "@/graphql/generated/types";
import { runGraphQL } from "@/graphql/runGraphQL";
import { formatApolloErrorForLog } from "@/graphql/useGraphQLError";
import { getLogger } from "@/utils/logger";
import { isAbortOrTeardownError, isPageHiddenTeardown } from "@/utils/requestAbort";

async function fetchClubMeta(shortName: string) {
  const data = runGraphQL(
    await apolloClient.query<GetClubMetaByShortNameQuery, GetClubMetaByShortNameQueryVariables>({
      query: GetClubMetaByShortNameDocument,
      variables: { shortName },
      fetchPolicy: "cache-first",
    }),
    { fallbackMessage: "Failed to load club details" },
  );
  return data.clubMetaByShortName ?? null;
}

const logger = getLogger("useUserClubHierarchy");

interface ClubNode {
  id: number;
  shortName: string;
  name: string;
  divisionId?: string;
  areaId?: string;
  locationLabel?: string;
}

/**
 * Flat district group: contains member clubs in the district plus the
 * division/area context (used for the expanded "metadata" section).
 */
interface DistrictGroup {
  id: string;
  /** Unique divisions where the user has clubs (or matches the current route) */
  divisions: DivisionMeta[];
  /** Member clubs in this district, sorted by name */
  clubs: ClubNode[];
}

interface DivisionMeta {
  id: string;
  /** Areas under this division where the user has clubs (or matches the current route) */
  areas: AreaMeta[];
}

interface AreaMeta {
  id: string;
}

interface MemberClubInfo {
  id: number;
  shortName: string;
  name: string;
  districtId: string;
  divisionId?: string;
  areaId?: string;
  isOnToastHost: boolean;
}

export interface VisitingHierarchyScope {
  districtId: string;
  divisionId?: string;
  areaId?: string;
  clubId?: number;
  clubShortName?: string;
  clubName?: string;
  isOnToastHost?: boolean;
}

interface ClubEntry {
  id: number;
  shortName: string;
  name: string;
  districtId: string;
  divisionId?: string;
  areaId?: string;
}

// Module-level state — shared across all callers so we don't refetch on every navigation.
const memberClubs = ref<MemberClubInfo[]>([]);
const loading = ref(false);
const loadedForUserKey = ref<string | null>(null);
const memberFallbackClubs = ref<Record<string, MemberClubInfo>>({});
// Cache for clubs not in the user's memberships (loaded on demand by route)
const extraClubs = ref<Record<string, MemberClubInfo>>({});

function isAuthMemberClub(shortName: string): boolean {
  const authStore = useAuthStore();
  return (authStore.userClubs ?? []).some((club) => club.shortName === shortName);
}

function getAuthMemberClub(shortName: string) {
  const authStore = useAuthStore();
  return (authStore.userClubs ?? []).find((club) => club.shortName === shortName);
}

function pruneMemberFallbackClubs() {
  const authStore = useAuthStore();
  const authClubShortNames = new Set((authStore.userClubs ?? []).map((club) => club.shortName));
  const loadedMemberShortNames = new Set(memberClubs.value.map((club) => club.shortName));
  const nextFallbackClubs = Object.fromEntries(
    Object.entries(memberFallbackClubs.value).filter(
      ([shortName]) => authClubShortNames.has(shortName) && !loadedMemberShortNames.has(shortName),
    ),
  );

  memberFallbackClubs.value = nextFallbackClubs;
}

function userKey(): string {
  const authStore = useAuthStore();
  return (authStore.userClubs ?? [])
    .map((c) => c.shortName)
    .sort()
    .join(",");
}

function normalizeOptionalId(value: unknown): string | undefined {
  if (value === null || value === undefined) return undefined;
  const normalized = String(value).trim();
  if (normalized.length === 0 || normalized === "undefined" || normalized === "null") {
    return undefined;
  }
  return normalized;
}

async function loadHierarchy(force = false): Promise<void> {
  const authStore = useAuthStore();
  const clubs = authStore.userClubs ?? [];
  const key = userKey();

  pruneMemberFallbackClubs();

  if (!force && loadedForUserKey.value === key) {
    return;
  }

  if (clubs.length === 0) {
    memberClubs.value = [];
    memberFallbackClubs.value = {};
    loadedForUserKey.value = key;
    return;
  }

  loading.value = true;
  try {
    const results = await Promise.all(
      clubs.map(async (club) => {
        try {
          const meta = await fetchClubMeta(club.shortName);
          if (!meta) return null;
          return {
            id: club.id,
            shortName: club.shortName,
            name: club.name,
            districtId: String(meta.district),
            divisionId: normalizeOptionalId(meta.division),
            areaId: normalizeOptionalId(meta.area),
            isOnToastHost: true,
          } as MemberClubInfo;
        } catch (err) {
          if (isAbortOrTeardownError(err) || isPageHiddenTeardown(err)) {
            logger.debug(`Hierarchy load aborted for club ${club.shortName}`, formatApolloErrorForLog(err));
          } else {
            logger.warn(`Failed to load hierarchy for club ${club.shortName}`, formatApolloErrorForLog(err));
          }
          return null;
        }
      }),
    );
    memberClubs.value = results.filter((r): r is MemberClubInfo => r !== null);
    loadedForUserKey.value = key;
    pruneMemberFallbackClubs();
  } finally {
    loading.value = false;
  }
}

async function ensureExtraClub(shortName: string): Promise<MemberClubInfo | null> {
  if (!shortName) return null;
  // Already in member clubs?
  const member = memberClubs.value.find((c) => c.shortName === shortName);
  if (member) return member;
  if (isAuthMemberClub(shortName)) {
    if (memberFallbackClubs.value[shortName]) return memberFallbackClubs.value[shortName];
    if (loadedForUserKey.value !== userKey() || loading.value) return null;

    const authClub = getAuthMemberClub(shortName);
    if (!authClub) return null;

    try {
      const meta = await fetchClubMeta(shortName);
      if (!meta) return null;
      const entry: MemberClubInfo = {
        id: authClub.id,
        shortName: authClub.shortName,
        name: authClub.name,
        districtId: String(meta.district),
        divisionId: normalizeOptionalId(meta.division),
        areaId: normalizeOptionalId(meta.area),
        isOnToastHost: true,
      };
      memberFallbackClubs.value = { ...memberFallbackClubs.value, [shortName]: entry };
      return entry;
    } catch (err) {
      if (isAbortOrTeardownError(err)) {
        logger.debug(`Fallback hierarchy load aborted for member club ${shortName}`, formatApolloErrorForLog(err));
      } else {
        logger.warn(`Failed to load fallback hierarchy for member club ${shortName}`, formatApolloErrorForLog(err));
      }
      return null;
    }
  }
  if (extraClubs.value[shortName]) return extraClubs.value[shortName];

  try {
    const meta = await fetchClubMeta(shortName);
    if (!meta) return null;
    const entry: MemberClubInfo = {
      id: meta.clubId,
      shortName: meta.shortName,
      name: meta.longName,
      districtId: String(meta.district),
      divisionId: normalizeOptionalId(meta.division),
      areaId: normalizeOptionalId(meta.area),
      isOnToastHost: meta.isOnToastHost,
    };
    extraClubs.value = { ...extraClubs.value, [shortName]: entry };
    return entry;
  } catch (err) {
    if (isAbortOrTeardownError(err)) {
      logger.debug(`Hierarchy load aborted for extra club ${shortName}`, formatApolloErrorForLog(err));
    } else {
      logger.warn(`Failed to load hierarchy for extra club ${shortName}`, formatApolloErrorForLog(err));
    }
    return null;
  }
}

function buildDistrictGroupsFromEntries(
  entries: ClubEntry[],
  opts: {
    currentDistrictId?: string;
    currentDivisionId?: string;
    currentAreaId?: string;
  },
): DistrictGroup[] {
  const groups = new Map<
    string,
    {
      clubs: ClubEntry[];
      divisions: Map<string, Set<string>>;
    }
  >();

  function ensureGroup(districtId: string) {
    if (!groups.has(districtId)) {
      groups.set(districtId, { clubs: [], divisions: new Map() });
    }
    return groups.get(districtId)!;
  }

  entries.forEach((entry) => {
    const group = ensureGroup(entry.districtId);
    if (!group.clubs.some((club) => club.id === entry.id)) {
      group.clubs.push({
        id: entry.id,
        shortName: entry.shortName,
        name: entry.name,
        districtId: entry.districtId,
        divisionId: entry.divisionId,
        areaId: entry.areaId,
      });
    }
    if (entry.divisionId) {
      if (!group.divisions.has(entry.divisionId)) {
        group.divisions.set(entry.divisionId, new Set());
      }
      if (entry.areaId) {
        group.divisions.get(entry.divisionId)!.add(entry.areaId);
      }
    }
  });

  const hasMatchingDistrictEntry = opts.currentDistrictId
    ? entries.some((entry) => entry.districtId === opts.currentDistrictId)
    : false;

  if (opts.currentDistrictId && hasMatchingDistrictEntry) {
    const group = ensureGroup(opts.currentDistrictId);
    if (opts.currentDivisionId) {
      if (!group.divisions.has(opts.currentDivisionId)) {
        group.divisions.set(opts.currentDivisionId, new Set());
      }
      if (opts.currentAreaId) {
        group.divisions.get(opts.currentDivisionId)!.add(opts.currentAreaId);
      }
    }
  }

  return Array.from(groups.entries())
    .map(([districtId, group]) => ({
      id: districtId,
      clubs: buildClubNodes(group.clubs),
      divisions: Array.from(group.divisions.entries())
        .map(([divisionId, areaSet]) => ({
          id: divisionId,
          areas: Array.from(areaSet)
            .map((id) => ({ id }))
            .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true })),
        }))
        .sort((a, b) => a.id.localeCompare(b.id)),
    }))
    .sort((a, b) => a.id.localeCompare(b.id, undefined, { numeric: true }));
}

function buildClubNodes(clubs: ClubEntry[]): ClubNode[] {
  const branchKeys = new Set(clubs.map((club) => `${club.divisionId ?? ""}|${club.areaId ?? ""}`));
  const hasAmbiguousBranches = branchKeys.size > 1;
  const distinctDivisions = new Set(
    clubs
      .map((club) => club.divisionId)
      .filter((divisionId): divisionId is string => typeof divisionId === "string" && divisionId.length > 0),
  );
  const useAreaOnlyLabel = distinctDivisions.size <= 1;

  return clubs
    .slice()
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((club) => ({
      id: club.id,
      shortName: club.shortName,
      name: club.name,
      divisionId: club.divisionId,
      areaId: club.areaId,
      locationLabel: hasAmbiguousBranches ? buildClubLocationLabel(club, useAreaOnlyLabel) : undefined,
    }));
}

function buildClubLocationLabel(
  club: Pick<ClubEntry, "divisionId" | "areaId">,
  useAreaOnlyLabel: boolean,
): string | undefined {
  if (useAreaOnlyLabel) {
    if (club.areaId) return `Area ${club.areaId}`;
    if (club.divisionId) return `Division ${club.divisionId}`;
    return undefined;
  }

  const parts = [];
  if (club.divisionId) {
    parts.push(`Division ${club.divisionId}`);
  }
  if (club.areaId) {
    parts.push(`Area ${club.areaId}`);
  }
  return parts.join(" • ") || undefined;
}

/**
 * Build a tree of districts/divisions/areas/clubs visible in the sidebar.
 *
 * @param currentDistrictId Optional current route district (added only when it matches an existing member district)
 * @param currentDivisionId Optional current route division
 * @param currentAreaId Optional current route area
 */
export function useUserClubHierarchy() {
  const authStore = useAuthStore();

  onMounted(() => {
    void loadHierarchy();
  });

  watch(
    () => userKey(),
    () => {
      void loadHierarchy(true);
    },
  );

  /**
   * Build the district-grouped tree (Option D structure).
   *
   * Each district contains:
   * - A flat alphabetical list of the user's member clubs
   * - Division/area metadata (only shown when district is expanded)
   *
   * The currently viewed district / division / area is included in the
   * metadata only when it matches an existing member district.
   */
  function buildDistrictGroups(opts: {
    currentDistrictId?: string;
    currentDivisionId?: string;
    currentAreaId?: string;
  }): DistrictGroup[] {
    const allClubs: ClubEntry[] = [];
    const seenShortNames = new Set<string>();

    function pushClubEntry(entry: ClubEntry) {
      if (seenShortNames.has(entry.shortName)) return;
      seenShortNames.add(entry.shortName);
      allClubs.push(entry);
    }

    memberClubs.value.forEach((c) => {
      pushClubEntry({
        id: c.id,
        shortName: c.shortName,
        name: c.name,
        districtId: c.districtId,
        divisionId: c.divisionId,
        areaId: c.areaId,
      });
    });

    Object.values(memberFallbackClubs.value).forEach((c) => {
      pushClubEntry({
        id: c.id,
        shortName: c.shortName,
        name: c.name,
        districtId: c.districtId,
        divisionId: c.divisionId,
        areaId: c.areaId,
      });
    });

    return buildDistrictGroupsFromEntries(allClubs, opts);
  }

  function buildDistrictGroupForClub(
    club: Pick<MemberClubInfo, "id" | "shortName" | "name" | "districtId" | "divisionId" | "areaId"> | null | undefined,
    opts: {
      currentDistrictId?: string;
      currentDivisionId?: string;
      currentAreaId?: string;
    },
  ): DistrictGroup | null {
    if (!club) return null;

    return (
      buildDistrictGroupsFromEntries(
        [
          {
            id: club.id,
            shortName: club.shortName,
            name: club.name,
            districtId: club.districtId,
            divisionId: club.divisionId,
            areaId: club.areaId,
          },
        ],
        opts,
      )[0] ?? null
    );
  }

  function buildVisitingDistrictGroup(
    shortName: string | undefined,
    opts: {
      currentDistrictId?: string;
      currentDivisionId?: string;
      currentAreaId?: string;
    },
  ): DistrictGroup | null {
    return buildDistrictGroupForClub(getVisitingClub(shortName), opts);
  }

  function getVisitingClub(shortName: string | undefined): MemberClubInfo | null {
    if (!shortName) return null;
    if (isAuthMemberClub(shortName)) return null;
    return extraClubs.value[shortName] ?? null;
  }

  function buildDistrictGroupForVisitingScope(scope: VisitingHierarchyScope | null | undefined): DistrictGroup | null {
    if (!scope?.districtId) return null;

    const clubs =
      typeof scope.clubId === "number" &&
      Number.isFinite(scope.clubId) &&
      typeof scope.clubShortName === "string" &&
      scope.clubShortName.length > 0 &&
      typeof scope.clubName === "string" &&
      scope.clubName.length > 0
        ? [
            {
              id: scope.clubId,
              shortName: scope.clubShortName,
              name: scope.clubName,
              divisionId: scope.divisionId,
              areaId: scope.areaId,
              locationLabel: undefined,
            },
          ]
        : [];

    const divisions = scope.divisionId
      ? [
          {
            id: scope.divisionId,
            areas: scope.areaId ? [{ id: scope.areaId }] : [],
          },
        ]
      : [];

    return {
      id: scope.districtId,
      clubs,
      divisions,
    };
  }

  return {
    loading: computed(() => loading.value),
    // True once an initial hierarchy load has completed for some user. Consumers
    // that prune persisted state against the model must wait for this so they
    // don't act on the empty pre-load model (loads in onMounted).
    loaded: computed(() => loadedForUserKey.value !== null),
    memberClubs: computed(() => memberClubs.value),
    buildDistrictGroups,
    buildDistrictGroupForClub,
    buildDistrictGroupForVisitingScope,
    buildVisitingDistrictGroup,
    getVisitingClub,
    ensureExtraClub,
    refresh: () => loadHierarchy(true),
    isAuthenticated: computed(() => authStore.isAuthenticated),
  };
}
